import React, { useMemo, useState } from 'react';
import IGuest from '@/models/guests/iguest';
import * as guestService from '@/services/guests-service';
import * as tripsService from '@/services/trips-service';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import ConfirmationModal from "@/components/modals/confirmation";
import Spinner from "@/components/spinner";
import EditGuestComponent from "./components/add-edit-guest";
import EditTripComponent from "./components/add-edit-trip";
import IGuestTrip from "@/models/trips/iguest-trip";
import ITrip from "@/models/trips/itrip";
import ViewTripPayments from "../guests/trip-payments/view";
import ITripPayment from "@/models/trip-payments/itrip-payment";
import Search from "../search/search";
import ISimpleGuest from "@/models/guests/isimple-guest";

interface IProps {
    guestTrip: IGuestTrip;
    isAdding: boolean;
    closeModal: (requiresUpdate: boolean) => void;
}

const AddOrEditGuestAndTrip: React.FC<IProps> = ({ guestTrip, isAdding, closeModal }) => {

    const [ guest, setGuest] = useState<IGuest>({} as IGuest);
    const [ trip, setTrip ] = useState<ITrip>({} as ITrip);
    const [ tripPayments, setTripPayments ] = useState<ITripPayment[]>([]);
    const [ returnGuestId, setReturnGuestId ] = useState<number>();
    const [ isAddingReturnGuest, setIsAddingReturnGuest ] = useState<boolean>(false);
    const [ isAddingInstructor, setIsAddingInstructor ] = useState<boolean>(guestTrip.isInstructor ? true : false);
    
    const [ guestIsLoading, setGuestIsLoading ] = useState<boolean>(true);
    const [ tripIsLoading, setTripIsLoading ] = useState<boolean>(true);

    const [ showConfirmationCloseModal, setShowConfirmationCloseModal ] = useState<boolean>(false);
    const [ showConfirmationDeletemodal, setShowConfirmationDeletemodal ] = useState<boolean>(false);

    // Validation
    const [ guestHasRequiredFormFields, setGuestHasRequiredFormFields ] = useState<boolean>();
    const [ guestHasFormData, setGuestHasFormData ] = useState<boolean>();
    const [ guestHasChanged, setGuestHasChanged ] = useState<boolean>();
    const [ tripHasRequiredFormFields, setTripHasRequiredFormFields ] = useState<boolean>();
    const [ tripHasFormData, setTripHasFormData ] = useState<boolean>();
    const [ tripHasChanged, setTripHasChanged ] = useState<boolean>();

    const addGuestAndTrip = async () => {
        if (isAddingInstructor) {
            const update = async () => {
                const guestResponse = await guestService.addGuest({
                    isInstructor: isAddingInstructor
                } as IGuest);
    
                await tripsService.addTrip({
                    guestId: guestResponse.id,
                    retreatId: guestTrip.retreatId,
                    bookedRoomTypeId: trip?.bookedRoomTypeId
                } as ITrip);
            }

            await toast.promise(
                update(),
                {
                    pending: 'Adding',
                    success: 'Added',
                    error: 'Something went wrong whilst trying to add the guest'
                }
            )

            closeModal(true);

        } else if (guestHasRequiredFormFields && tripHasRequiredFormFields) {
            const update = async () => {

                let guestResponse;

                if (isAddingReturnGuest) {
                    guestResponse = await guestService.updateGuest(guest);
                } else {
                    guestResponse = await guestService.addGuest({
                        firstName: guest?.firstName,
                        lastName: guest?.lastName,
                        emailAddress: guest?.emailAddress,
                        phoneNumber: guest?.phoneNumber,
                        dietaryRequirements: guest?.dietaryRequirements,
                        notes: guest?.notes,
                        isInstructor: isAddingInstructor,
                    } as IGuest);
                }

                await tripsService.addTrip({
                    guestId: guestResponse.id,
                    retreatId: guestTrip.retreatId,
                    reservationDate: trip.reservationDate,
                    discount: trip.discount,
                    fullPrice: trip.fullPrice,
                    isCancelled: trip.isCancelled,
                    bookedRoomTypeId: trip.bookedRoomTypeId,
                    bookedViaId: trip.bookedViaId,
                    staffNotes: trip.staffNotes,
                    guestNotes: trip.guestNotes,
                    tripPayments: tripPayments,
                } as ITrip)
            }

            await toast.promise(
                update(),
                {
                    pending: 'Adding',
                    success: 'Added',
                    error: 'Something went wrong whilst trying to add the guest'
                }
            )

            closeModal(true);
        }
    }

    const updateGuestAndTrip = async () => {
        const updateGuest = guest !== undefined && guestHasChanged && guestHasRequiredFormFields;
        const updateTrip = trip !== undefined && tripHasChanged && tripHasRequiredFormFields;

        if (isAddingInstructor) {
            const update = async () => {

                await tripsService.updateTrip(trip);
            }

            await toast.promise(
                update(),
                {
                    pending: 'Updating',
                    success: 'Updated',
                    error: 'Something went wrong whilst trying to update'
                }
            )

            closeModal(true);

        } else if (updateGuest || updateTrip) {
            const update = async () => {

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let promises: any[] = [];

                if (updateGuest) {
                    promises = [...promises, guestService.updateGuest(guest)];
                }

                if (updateTrip) {
                    promises = [...promises, tripsService.updateTrip(trip)];
                }

                await Promise.all(promises);
            }

            await toast.promise(
                update(),
                {
                    pending: 'Updating',
                    success: 'Updated',
                    error: 'Something went wrong whilst trying to update'
                }
            )

            closeModal(true);
        } else {
            toast.error("There has been an uncaught issue with updating the guest");
        }
    }

    function requestToCloseModal() {
        if (
            (isAdding && (guestHasFormData || tripHasFormData)) ||
            (!isAdding && (guestHasChanged || tripHasChanged))
        ) {
            setShowConfirmationCloseModal(true);
        }
        else {
            closeModal(false);
        }
    }

    function receivedConfirmationCloseRequest(wantsToClose: boolean) {
        setShowConfirmationCloseModal(false);
        
        if (wantsToClose) {
            closeModal(false);
        }
    }

    async function requestToDelete() {
        setShowConfirmationDeletemodal(true);
    }

    async function receivedConfirmationDeleteRequest(wantsToDelete: boolean) {
        setShowConfirmationDeletemodal(false);
        
        if (wantsToDelete) {
            const remove = async () => {
                await tripsService.deleteTrip(guestTrip.tripId, guestTrip.guestId);
            }
    
            await toast.promise(
                remove(),
                {
                    pending: "Removing guest",
                    success: "Guest removed",
                    error: "Something went wrong whilst trying to remove the guest"
                }
            )
            
            closeModal(true);
        }
    }

    function updateCreationType(isInstructor: boolean, isReturnGuest: boolean) {
        setIsAddingInstructor(isInstructor);
        setIsAddingReturnGuest(isReturnGuest);
    }

    function guestModelValidation(GuestHasRequiredFormFields: boolean, GuestHasFormData: boolean, GuestHasChanged: boolean, Guest: IGuest){
        setGuestHasRequiredFormFields(GuestHasRequiredFormFields);
        setGuestHasFormData(GuestHasFormData);
        setGuestHasChanged(GuestHasChanged);
        setGuest(Guest);
    }

    function tripModelValidation(TripHasRequiredFormFields: boolean, TripHasFormData: boolean, TripHasChanged: boolean, Trip: ITrip){
        setTripHasRequiredFormFields(TripHasRequiredFormFields);
        setTripHasFormData(TripHasFormData);
        setTripHasChanged(TripHasChanged);
        setTrip(Trip);
    }

    const showGuestAndTripPaymentComponent = useMemo(() => {
        if (isAdding && isAddingInstructor) {
            return false;
        }

        if (!isAdding && guestTrip.isInstructor === true) {
            return false;
        }

        return true;
    }, [isAdding, isAddingInstructor, guestTrip])

    return (
        <div className="w-11/12 lg:w-3/4 mx-auto">
            <div className='bg-br-blue-300'>
                
                <Spinner isLoading={guestIsLoading && tripIsLoading}>
                    <div>
                        <h1 className="text-4xl md:text-6xl font-thin text-center pb-1 pt-3">{isAdding ? "Add" : "Edit"} guest</h1>
                        <hr className="mx-auto mt-3" />

                        {/* The top buttons for adding a new guest / return guest / instructor  */}
                        {isAdding && (
                            <>
                                <div className="flex justify-center cursor-pointer mt-1">
                                    <button className={`px-4 py-2 text-xl ${!isAddingInstructor && !isAddingReturnGuest ? `underline` : 'hover:underline decoration-slate-300'}`} onClick={() => updateCreationType(false, false)}>
                                        New guest
                                    </button>

                                    <button className={`px-4 py-2 text-xl ${!isAddingInstructor && isAddingReturnGuest ? `underline` : 'hover:underline decoration-slate-300'}`} onClick={() => updateCreationType(false, true)}>
                                        Return guest
                                    </button>
            
                                    <button className={`px-4 py-2 text-xl ${isAddingInstructor ? `underline` : 'hover:underline decoration-slate-300'}`} onClick={() => updateCreationType(true, false)}>
                                        Instructor
                                    </button>
                                </div>

                                {isAddingReturnGuest && (
                                    <>
                                        {!returnGuestId ? (
                                            <div className="my-4 px-4">
                                                <Search compact={true} selectedGuest={(guest: ISimpleGuest) => setReturnGuestId(guest.id)} lighterColours={true} />
                                            </div>  
                                        ) : (
                                            <div className="flex justify-center cursor-pointer mt-1">
                                                <button className="px-3 py-1 bg-blue-500 hover:brightness-110" onClick={() => setReturnGuestId(undefined)} >Search again</button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {(!isAddingReturnGuest || (isAddingReturnGuest && returnGuestId)) && (
                        <>
                            <div className='mx-2 grid grid-cols-2 pb-4 pt-2'>

                                {showGuestAndTripPaymentComponent && (
                                    <>
                                        <EditGuestComponent id={guestTrip.guestId ?? returnGuestId} modelValidation={(a, b, c, d) => guestModelValidation(a, b, c, d)} isLoading={(response) => setGuestIsLoading(response)} />
                                    </>
                                )}

                                <EditTripComponent id={guestTrip.tripId} isInstructor={guestTrip?.isInstructor || isAddingInstructor} modelValidation={(a, b, c, d) => tripModelValidation(a, b, c, d)} isLoading={(response) => setTripIsLoading(response)} />

                            </div>

                            {showGuestAndTripPaymentComponent && (
                                <div className="col-span-2">
                                    <ViewTripPayments 
                                        guestId={guestTrip.guestId}
                                        tripId={guestTrip.tripId}
                                        updateTripPayments={isAdding ? setTripPayments : undefined}
                                        defaultNewDate={isAdding ? trip.reservationDate : undefined}
                                        retreatTotal={(trip.fullPrice ??  0) * ((100-Number(trip.discount ?? 0))/100)}
                                    />
                                </div>
                            )}
                        </>
                        )}

                        <div className="flex justify-end mx-5">
                            {isAdding ? (
                                <>
                                    <button className="px-3 py-1 mx-1 mb-2 bg-green-500 hover:brightness-110 disabled:opacity-50" onClick={() => addGuestAndTrip()} disabled={(isAddingInstructor && !trip.bookedRoomTypeId) || (!isAddingInstructor && (!guestHasRequiredFormFields || !tripHasRequiredFormFields))}>Add</button>
                                    <button className="px-3 py-1 mx-1 mb-2 bg-red-600 hover:brightness-110" onClick={requestToCloseModal}>Cancel</button>
                                </>
                            ) : (
                                <>
                                    <button className="px-3 py-1 mx-1 mb-2 bg-green-500 hover:brightness-110 disabled:opacity-50" onClick={updateGuestAndTrip} disabled={(isAddingInstructor && !trip.bookedRoomTypeId) || (!isAddingInstructor && (!guestHasRequiredFormFields || !tripHasRequiredFormFields || (!guestHasChanged && !tripHasChanged)))}>Save</button>

                                    <button className="px-3 py-1 mx-1 mb-2 bg-red-600 hover:brightness-110" onClick={requestToCloseModal}>Cancel</button>

                                    <button className="px-3 py-1 mx-1 mb-2 bg-red-600 hover:brightness-110" onClick={requestToDelete} title="Delete. If this is the only trip this guest has, it will delete the entire guest, if they've been on multiple trips, this will delete only this trip, but leave the guest on the system."><i className="fas fa-trash-alt text-white" /></button>
                                </>
                            )}

                        </div>

                        {showConfirmationCloseModal && <ConfirmationModal confirmationText={`Are you sure you want to cancel ${isAdding ? "adding" : "updating"} this guest?`} closeModal={(response) => receivedConfirmationCloseRequest(response)} />}
                        {showConfirmationDeletemodal && <ConfirmationModal confirmationText="Are you sure you want to delete this guest?" closeModal={(response) => receivedConfirmationDeleteRequest(response)} />}
                    </div>
                </Spinner>
            </div>
        </div>
    );
}

export default AddOrEditGuestAndTrip;